import React, { useState, useRef, useCallback } from 'react';
import { authHeaders } from '../utils/authService';

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 720 },
  section: { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 20 },
  h2: { fontFamily: 'var(--font-heading)', fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-text)', margin: '0 0 4px' },
  sub: { fontSize: 13, color: 'var(--color-text-dim)', margin: '0 0 16px' },
  label: { fontFamily: 'var(--font-heading)', fontSize: 9, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-text-dim)', display: 'block', marginBottom: 4 },
  input: { padding: '10px 12px', background: 'var(--color-surface-light)', border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box', marginBottom: 14 },
  textarea: { padding: '10px 12px', background: 'var(--color-surface-light)', border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box', minHeight: 160, resize: 'vertical', marginBottom: 14 },
  btnRow: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' },
  btn: { padding: '10px 18px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface-light)', color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer' },
  btnPrimary: { padding: '10px 18px', border: 'none', borderRadius: 6, background: 'var(--color-primary)', color: '#fff', fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer' },
  btnDanger: { padding: '10px 18px', border: 'none', borderRadius: 6, background: 'var(--color-danger)', color: '#fff', fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer' },
  status: { fontSize: 13, color: 'var(--color-text-dim)', marginTop: 4 },
  progress: { marginTop: 14, padding: 14, background: 'var(--color-surface-light)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 },
  fail: { color: 'var(--color-danger)', fontFamily: 'var(--font-data)', fontSize: 12 },
};

export default function EmailPanel() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(null);
  const [welcomeStatus, setWelcomeStatus] = useState('');
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollProgress = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`/api/admin/broadcast/${jobId}`, { headers });
        const data = await res.json();
        if (!res.ok) return;
        setProgress(data);
        if (data.done) {
          stopPolling();
          setSending(false);
          setStatus(`Done — ${data.sent} sent, ${data.failed} failed.`);
        }
      } catch {
        /* keep polling; transient errors are fine */
      }
    }, 1500);
  }, [stopPolling]);

  const send = useCallback(async (mode) => {
    if (!subject.trim() || !body.trim()) {
      setStatus('Subject and message are required.');
      return;
    }
    if (mode === 'all') {
      const ok = window.confirm('Send this email to ALL registered users? This cannot be undone.');
      if (!ok) return;
    }
    setSending(true);
    setProgress(null);
    setStatus(mode === 'test' ? 'Sending test to you…' : 'Starting broadcast…');
    try {
      const headers = await authHeaders();
      const res = await fetch('/api/admin/broadcast', {
        method: 'POST',
        headers,
        body: JSON.stringify({ subject, body, mode }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSending(false);
        setStatus(data.error || 'Failed to start broadcast.');
        return;
      }
      setStatus(`Sending to ${data.total} recipient(s)…`);
      pollProgress(data.jobId);
    } catch (err) {
      setSending(false);
      setStatus(`Error: ${err.message}`);
    }
  }, [subject, body, pollProgress]);

  const sendTestWelcome = useCallback(async () => {
    setWelcomeStatus('Sending…');
    try {
      const headers = await authHeaders();
      const res = await fetch('/api/admin/send-test-welcome', { method: 'POST', headers });
      const data = await res.json();
      setWelcomeStatus(res.ok ? `Sent to ${data.to}` : (data.error || 'Failed to send.'));
    } catch (err) {
      setWelcomeStatus(`Error: ${err.message}`);
    }
  }, []);

  return (
    <div style={S.wrap}>
      {/* Broadcast composer */}
      <div style={S.section}>
        <h2 style={S.h2}>Broadcast Email</h2>
        <p style={S.sub}>Send a message to every registered user (individually — no shared To/CC). Always send a test to yourself first.</p>

        <label style={S.label}>Subject</label>
        <input
          style={S.input}
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject line"
          disabled={sending}
        />

        <label style={S.label}>Message</label>
        <textarea
          style={S.textarea}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your message. Blank lines start new paragraphs. It will be wrapped in the RaceVoice branded template."
          disabled={sending}
        />

        <div style={S.btnRow}>
          <button style={S.btn} onClick={() => send('test')} disabled={sending}>Send test to me</button>
          <button style={S.btnDanger} onClick={() => send('all')} disabled={sending}>Send to all users</button>
        </div>

        {status && <p style={S.status}>{status}</p>}

        {progress && (
          <div style={S.progress}>
            <div>Total: {progress.total} · Sent: {progress.sent} · Failed: {progress.failed} {progress.done ? '· Done' : '· Sending…'}</div>
            {progress.failures && progress.failures.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {progress.failures.map((f, i) => (
                  <div key={i} style={S.fail}>{f.email}: {f.error}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Welcome email preview */}
      <div style={S.section}>
        <h2 style={S.h2}>Welcome Email</h2>
        <p style={S.sub}>New users automatically receive the welcome email on signup. Send yourself a copy to preview the exact email.</p>
        <div style={S.btnRow}>
          <button style={S.btnPrimary} onClick={sendTestWelcome}>Send Test Welcome Email</button>
          {welcomeStatus && <span style={S.status}>{welcomeStatus}</span>}
        </div>
      </div>
    </div>
  );
}
